import React, {useState} from 'react';
import moment from 'moment';
import {List} from 'immutable';

import apolloClient from 'apollo/client';
import notifier from '@albert-io/notifier';
import logger from 'lib/logger';
import {isApolloError} from 'lib/normalizeError';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
import {createTypeObject, createYearObject, summarizePublishedQuestions} from '../helpers/utils';
import {
  AuthorProfileDocument,
  AuthorPublishedActivityDocument,
  AuthorPublishedQuestionMetricsDocument,
  AuthorQuestionCountsDocument,
  AuthorRecentQuestionsDocument,
  useAuthorDashboardRecentFeedbackQuery
} from '../AuthorDashboard.queries.generated';

export const AuthorDashboardContext = React.createContext();

const PAGE_SIZE = 250;

// A ceiling on the metrics paging loop below, so a server that ignored `offset`
// could not spin it forever. 25 pages is far past any real author's output.
const MAX_METRICS_PAGES = 25;

const DATE_RANGES = {
  'last-year': [
    moment().subtract(1, 'years').startOf('year').valueOf(),
    moment().startOf('year').valueOf()
  ],
  'this-year': [
    moment().startOf('year').valueOf(),
    moment().add(1, 'years').startOf('year').valueOf()
  ]
};

const SHOW_MORE_RATE = 8;

class AuthorDashboardProviderClass extends React.Component {
  constructor() {
    super();
    this.state = {
      data: null,
      isLoaded: false,
      indexOfActivity: SHOW_MORE_RATE,
      count: {
        allTime: null,
        thisMonth: null,
        thisYear: null
      },
      recentQuestions: null,
      questionDifficulties: null,
      questionTypes: null,
      handleShowMore: this.handleShowMore,
      handleDateChange: this.handleDateChange,
      handleSubjectSelection: this.handleSubjectSelection,
      storeCustomStartDate: this.storeCustomStartDate,
      storeCustomEndDate: this.storeCustomEndDate,
      dateRange: DATE_RANGES['this-year'],
      publishedSubjects: [],
      selectedSubjects: [],
      startDate: 'initial',
      endDate: 'initial',
      noActivity: false,
      noRecent: false,
      isMetricsLoaded: false,
      selectValue: '',
      isNewAuthor: false
    };
  }

  /* Lifecycle methods */
  async componentDidMount() {
    this.calculateQuestionMetrics(DATE_RANGES['this-year'], this.state.publishedSubjects);
    this.fetchAuthorInfoWhenFeedbackReady();

    const {data} = await apolloClient.query({
      query: AuthorQuestionCountsDocument,
      variables: {
        authorId: sessionStore.getUserId(),
        yearStart: moment().startOf('year').toISOString(),
        monthStart: moment().startOf('month').toISOString()
      }
    });

    this.setState({
      count: {
        allTime: data.allTime.count,
        thisYear: data.thisYear.count,
        thisMonth: data.thisMonth.count
      },
      isNewAuthor: data.authored.count === 0
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.feedbackLoading && !this.props.feedbackLoading) {
      this.fetchAuthorInfoWhenFeedbackReady();
    }
  }

  // The recent-questions query needs the feedback question ids, so the fetch
  // chain waits for the feedback query (run by the wrapper below) to settle.
  fetchAuthorInfoWhenFeedbackReady() {
    if (!this.props.feedbackLoading) {
      this.fetchAuthorInfo().catch((error) => {
        logger.error('AuthorDashboard: author info fetch failed', error);
        // GraphQL failures already reach Honeybadger through the Apollo error link.
        if (!isApolloError(error)) {
          notifier.notify(error, {
            message: 'AuthorDashboard: author info fetch failed',
            tags: 'AuthorDashboard'
          });
        }
      });
    }
  }

  /* Event Handlers */

  handleShowMore = () => {
    this.setState(({indexOfActivity}) => {
      return {indexOfActivity: indexOfActivity + SHOW_MORE_RATE};
    });
  };

  handleDateChange = (e) => {
    let dateRange;
    if (e.target.value === 'this-year' || e.target.value === 'last-year') {
      dateRange = DATE_RANGES[e.target.value];
      this.setState({dateRange, selectValue: e.target.value}, () => {
        this.calculateQuestionMetrics(this.state.dateRange, this.state.selectedSubjects);
      });
    } else {
      // handle custom case, where we dont want to caluclate the metrics just yet.
      this.setState({selectValue: e.target.value});
    }
  };

  handleSubjectSelection = (e, subjectIndex) => {
    const subject = this.state.publishedSubjects.get(subjectIndex);
    const {selectedSubjects} = this.state;

    if (!selectedSubjects.includes(subject)) {
      this.setState({selectedSubjects: selectedSubjects.concat(subject)}, () => {
        this.calculateQuestionMetrics(this.state.dateRange, this.state.selectedSubjects);
      });
    } else {
      this.setState(
        {selectedSubjects: selectedSubjects.splice(selectedSubjects.indexOf(subject), 1)},
        () => {
          if (this.state.selectedSubjects.size !== 0) {
            this.calculateQuestionMetrics(this.state.dateRange, this.state.selectedSubjects);
          } else {
            this.setState({
              publishedQuestionDates: createYearObject(),
              questionDifficulties: {all: 0, easy: 0, moderate: 0, difficult: 0},
              questionTypes: createTypeObject()
            });
          }
        }
      );
    }
  };

  storeCustomStartDate = (startDate) => {
    this.setState(
      {
        startDate,
        dateRange: [startDate, this.state.endDate]
      },
      () => {
        const now = moment().endOf('day').valueOf();
        const yearAfterStartDate = moment(this.state.startDate).add(1, 'years');
        const isBefore = moment(this.state.endDate).isBefore(yearAfterStartDate);
        if (
          this.state.startDate &&
          this.state.endDate &&
          this.state.startDate <= this.state.endDate &&
          this.state.endDate <= now &&
          isBefore
        ) {
          this.calculateQuestionMetrics(this.state.dateRange, this.state.selectedSubjects);
        }
      }
    );
  };

  storeCustomEndDate = (endDate) => {
    this.setState(
      {
        endDate,
        dateRange: [this.state.startDate, endDate]
      },
      () => {
        const now = moment().endOf('day').valueOf();
        const yearAfterStartDate = moment(this.state.startDate).add(1, 'years');

        const isBefore = moment(this.state.endDate).isBefore(yearAfterStartDate);

        if (
          this.state.startDate &&
          this.state.endDate &&
          this.state.startDate <= this.state.endDate &&
          this.state.endDate <= now &&
          isBefore
        ) {
          this.calculateQuestionMetrics(this.state.dateRange, this.state.selectedSubjects);
        }
      }
    );
  };

  /* Calculations */
  calculateQuestionMetrics = async (dateRange, selectedSubjects) => {
    this.setState({isMetricsLoaded: false});

    const filter = {
      authorId: {eq: sessionStore.getUserId()},
      status: {eq: 'PUBLISHED'}
    };

    if (dateRange) {
      filter.publishedAt = {
        greaterThanOrEqual: moment(dateRange[0]).toISOString(),
        lessThanOrEqual: moment(dateRange[1]).toISOString()
      };
    }

    // Empty means "no subject narrowing" — that is the state on first load,
    // before the author's subjects have arrived. Deselecting every subject in
    // the dropdown is handled by the caller, which zeroes the panel instead.
    if (selectedSubjects.size > 0) {
      filter.unfilteredSubject = {name: {in: selectedSubjects.toArray()}};
    }

    const publishedQuestions = await this.fetchAllPublishedQuestions(filter);
    const summary = summarizePublishedQuestions(publishedQuestions);

    this.setState({...summary, isMetricsLoaded: true});
  };

  /**
   * The metrics panel counts by month, difficulty and type, so it needs every
   * matching question rather than a page of them. A page is capped at 250, so
   * keep asking until a short one comes back.
   */
  async fetchAllPublishedQuestions(filter) {
    const questions = [];

    for (let page = 0; page < MAX_METRICS_PAGES; page++) {
      const {data} = await apolloClient.query({
        query: AuthorPublishedQuestionMetricsDocument,
        variables: {filter, offset: page * PAGE_SIZE}
      });

      const results = data?.questionsOffset?.results ?? [];
      questions.push(...results);

      if (results.length < PAGE_SIZE) {
        break;
      }
    }

    return questions;
  }

  /* Fetchers/Getters */

  fetchAuthorInfo = async () => {
    const authorId = sessionStore.getUserId();
    const publishedSince = moment().subtract(3, 'months').toISOString();
    const feedbackQuestionIds = [...new Set(this.props.recentFeedback.map((fb) => fb.questionId))];

    const [profile, recent, activity] = await Promise.all([
      apolloClient.query({query: AuthorProfileDocument, variables: {authorId}}),
      apolloClient.query({
        query: AuthorRecentQuestionsDocument,
        variables: {authorId, publishedSince, feedbackQuestionIds}
      }),
      apolloClient.query({
        query: AuthorPublishedActivityDocument,
        variables: {authorId, publishedSince}
      })
    ]);

    const data = profile.data?.user ?? null;
    const publishedSubjects = this.getPublishedSubjectNames(data?.subjectAuthors ?? []);
    const activityItems = this.getActivityItems(
      activity.data?.questionsOffset?.results ?? [],
      this.props.recentFeedback
    );
    // Already ordered most-recently-edited first by the query.
    const recentQuestions = (recent.data?.questionsOffset?.results ?? []).slice(0, 4);

    this.setState({
      isLoaded: true,
      data,
      activityItems,
      recentQuestions,
      noRecent: recentQuestions.length === 0,
      selectedSubjects: publishedSubjects,
      publishedSubjects
    });
  };

  getPublishedSubjectNames = (subjectAuthors) => {
    return List(subjectAuthors.map((subjectAuthor) => subjectAuthor.unfilteredSubject?.name))
      .filter(Boolean)
      .toSet()
      .toList();
  };

  getActivityItems = (publishedQuestions, recentFeedback = []) => {
    const activityItems = [];

    publishedQuestions.forEach((question) => {
      if (!question.publishedAt) {
        return;
      }
      const publishedAt = moment(question.publishedAt);
      activityItems.push({
        type: 'publishedQuestion',
        time: publishedAt,
        setId: question.questionSetId,
        subject: question.unfilteredSubject?.name,
        questionId: question.id,
        publishedAt,
        publishedAtDisplay: publishedAt.format('DD MMMM YYYY hh:mm A'),
        title: question.title
      });
    });

    recentFeedback.forEach((fb) => {
      const updatedAt = moment(fb.updatedAt);
      activityItems.push({
        type: 'feedback',
        time: updatedAt,
        setId: fb.question?.questionSetId,
        subject: fb.question?.unfilteredSubject?.name,
        questionId: fb.questionId,
        title: fb.question?.title,
        user: `${fb.userInfo?.firstName ?? ''} ${fb.userInfo?.lastName ?? ''}`.trim(),
        message: fb.message,
        updatedAt,
        updatedAtDisplay: updatedAt.format('DD MMMM YYYY hh:mm A'),
        feedbackId: fb.id
      });
    });
    const sortedActivityItems = activityItems.sort((item1, item2) => {
      return item1.time.isBefore(item2.time) ? 1 : -1;
    });

    // for empty state
    if (sortedActivityItems.length === 0) {
      this.setState({noActivity: true});
    }
    return sortedActivityItems;
  };

  render() {
    return (
      <AuthorDashboardContext.Provider value={this.state}>
        {this.props.children}
      </AuthorDashboardContext.Provider>
    );
  }
}

/**
 * Runs the Apollo feedback query (hooks can't live in the class above) and
 * feeds the results in as props; the class kicks its fetch chain once the
 * query settles.
 */
const AuthorDashboardProvider = ({children}) => {
  // No 'use memo' — inert until AuthorDashboard joins webpack's REACT_COMPILER_DIRS include-list.

  // Stable across renders so the query isn't re-issued with a shifting window.
  const [updatedSince] = useState(() => moment().subtract(3, 'months').toISOString());
  const userId = useFluxStore(sessionStore, (s) => s.getUserId());
  const {data, loading} = useAuthorDashboardRecentFeedbackQuery({
    variables: {updatedSince, userId}
  });
  const recentFeedback = data?.authoringFeedbacks?.results ?? [];

  return (
    <AuthorDashboardProviderClass recentFeedback={recentFeedback} feedbackLoading={loading}>
      {children}
    </AuthorDashboardProviderClass>
  );
};

export default AuthorDashboardProvider;
